import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccountPath } from '../../hooks/useAccountPath.js';
import { useAuthStore } from '../../stores/authStore.js';
import { formatBytes } from '../../utils/formatBytes.js';
import { TIER_CREDITS, TIER_LABELS } from '../../utils/tierConstants.js';
import { TrialCreditsBalance } from '../common/TrialCreditsBalance.jsx';
import { visionPath as appHref } from '../../utils/webAppLinks.js';
import { isPlainLeftClick } from './isPlainLeftClick.js';
import { IconArrowLeft, IconBolt } from './SidebarIcons.jsx';
import { SidebarStyle, usageLevel, usagePercentage } from './sidebarStyle.js';

const { Account, Foot } = SidebarStyle;
// The account row below gives up this much height in the rail; the foot takes it, with the same
// transition, so the cards stay put while they fade
const RAIL_DROP = 2 * (Account.INSET - SidebarStyle.EDGE_PADDING);
const BILLING = '/billing';
const CREDITS = '/billing?tab=credits';
const STORAGE = '/storage';

export function hasVisionPlan(status) {
  return status === 'active' || status === 'canceling' || status === 'past_due';
}

export function VisionFoot({ geometry, fluid = false }) {
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();
  const account = useAccountPath();

  if (!user) return null;

  const hasPlan = hasVisionPlan(user.subscription_status);
  const storageUsed = Number(user.storage_used) || 0;
  const storageLimit = Number(user.storage_limit) || 0;
  const purchased = user.purchased_credits || 0;
  const total = hasPlan ? TIER_CREDITS[user.subscription_tier] || 0 : 0;

  const go = (page) => (event) => {
    if (!isPlainLeftClick(event)) return;
    event.preventDefault();
    navigate(account(page));
  };

  return (
    <div
      className={`vs-foot${geometry.rail ? ' is-hidden' : ''}`}
      aria-hidden={geometry.rail}
      style={geometry.rail ? { paddingBottom: RAIL_DROP } : undefined}
    >
      <div className="vs-foot-body">
        <div className="vs-divider" />
        <div className="vs-foot-cards" style={{ width: fluid ? '100%' : SidebarStyle.WIDTH }}>
          {!hasPlan && (
            <a className="vs-subscribe" href={appHref(account(BILLING))} onClick={go(BILLING)}>
              <span className="vs-well">
                <IconBolt size={14} />
              </span>
              <span className="vs-card-text">
                <span className="vs-card-title">Subscribe</span>
                <span className="vs-card-hint">More credits and storage</span>
              </span>
              <span style={{ marginLeft: 'auto', display: 'inline-flex', opacity: 0.5 }}>
                <IconArrowLeft size={12} style={{ transform: 'rotate(180deg)' }} />
              </span>
            </a>
          )}

          {!hasPlan && <TrialCreditsBalance user={user} variant="sidebar" />}

          {(storageLimit > 0 || storageUsed > 0) && (
            <StorageBlock
              used={storageUsed}
              limit={storageLimit}
              href={appHref(account(STORAGE))}
              onOpen={go(STORAGE)}
            />
          )}

          {hasPlan && total > 0 && (
            <PlanBlock
              tier={user.subscription_tier}
              remaining={user.subscription_credits || 0}
              total={total}
              periodEnd={user.subscription_period_end}
              href={appHref(account(BILLING))}
              onOpen={go(BILLING)}
            />
          )}

          <a className="vs-bank" href={appHref(account(CREDITS))} aria-label="Credit Bank" onClick={go(CREDITS)}>
            <span style={{ display: 'inline-flex', flex: 'none', opacity: purchased === 0 ? 0.3 : 1 }}>
              <IconBolt size={Foot.BOLT_HEIGHT} style={{ color: 'var(--vs-primary)' }} />
            </span>
            <span className="vs-card-text">
              <span className="vs-bank-count">{purchased}</span>
              <span className="vs-small">Credit Bank</span>
            </span>
            {purchased === 0 && <span className="vs-bank-get">Get Credits</span>}
          </a>
        </div>
      </div>
    </div>
  );
}

function StorageBlock({ used, limit, href, onOpen }) {
  const percent = usagePercentage(used, limit);
  const level = usageLevel(percent);

  return (
    <a className="vs-panel" href={href} onClick={onOpen}>
      <span className="vs-line-row" style={{ marginBottom: 8 }}>
        <span className="vs-title" style={{ fontSize: 14 }}>Storage</span>
        <span className={`vs-value vs-${level}`}>
          {formatBytes(used)} of {formatBytes(limit)}
        </span>
      </span>
      <span
        className="vs-track"
        role="progressbar"
        aria-label="Storage"
        aria-valuenow={Math.round(percent)}
        aria-valuemin={0}
        aria-valuemax={100}
        style={{ display: 'block', height: Foot.STORAGE_BAR_HEIGHT }}
      >
        <span
          className={`vs-fill vs-fill-${level}`}
          style={{ display: 'block', width: `${Math.max(percent, Foot.MIN_FILL_PERCENT)}%` }}
        />
      </span>
      {level === 'critical' && (
        <span className="vs-note" style={{ display: 'block' }}>
          Storage is almost full. Free up space or upgrade your plan.
        </span>
      )}
    </a>
  );
}

function PlanBlock({ tier, remaining, total, periodEnd, href, onOpen }) {
  const usedPercent = usagePercentage(total - remaining, total);
  const level = usageLevel(usedPercent);
  const resets = periodEnd
    ? new Date(periodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null;

  return (
    <a className="vs-panel" href={href} onClick={onOpen}>
      <span className="vs-line-row" style={{ marginBottom: 4 }}>
        <span className="vs-title" style={{ fontSize: 12 }}>{TIER_LABELS[tier] || tier} Plan</span>
        <span className={`vs-value vs-${level}`}>
          {remaining} / {total}
        </span>
      </span>
      <span
        className="vs-track"
        role="progressbar"
        aria-label="Credits"
        aria-valuenow={remaining}
        aria-valuemin={0}
        aria-valuemax={total}
        style={{ display: 'block', height: Foot.PLAN_BAR_HEIGHT }}
      >
        <span
          className={`vs-fill vs-fill-${level}`}
          style={{ display: 'block', width: `${Math.max(100 - usedPercent, Foot.MIN_FILL_PERCENT)}%` }}
        />
      </span>
      <span className="vs-line-row" style={{ marginTop: 2 }}>
        <span className="vs-small">{resets ? `Resets ${resets}` : ''}</span>
        <span className="vs-small">Credits</span>
      </span>
    </a>
  );
}
